import { useEffect, useRef, useState, forwardRef, Ref } from "react";
import { EVENTS, cache, getRenderingEngine } from "@cornerstonejs/core";
import {
  Enums,
  segmentation as cstSegmentation,
  annotation,
} from "@cornerstonejs/tools";
import { initViewer } from "lib/cornerstone/setupCornerstoneViewer";
import { useSnapshot } from "valtio";
import settingsState from "stores/settings";
import uiState, { toggleExpandedPane } from "stores/ui";
import {
  crosshairRenderingListener,
  voiModifiedListener,
} from "lib/cornerstone/helpers/listeners";
import { useNotify } from "react-admin";
import viewerState, { buildNiftiMetadata } from "stores/viewer";
import useViewerParams from "shared/hooks/useViewerParams";
import getIds from "lib/cornerstone/helpers/getIds";

import { discoverNiftiFiles } from "../../../../lib/cornerstone/helpers/niftiDiscovery";
import { ENTRYPOINT } from "config/api";
import {
  buildPetFiles,
  isPetModality,
} from "lib/cornerstone/helpers/niftiDiscovery";

export interface ViewerProps {
  id: string;
}

type ViewerPhase = "idle" | "discovering" | "loading" | "ready" | "error";

function Viewer({ id }: ViewerProps) {
  const ui = useSnapshot(uiState);
  const snap = useSnapshot(viewerState);
  const notify = useNotify();
  const { patient_id, acquisition } = useViewerParams();
  const viewerRef1 = useRef<HTMLDivElement>(null);
  const viewerRef2 = useRef<HTMLDivElement>(null);
  const viewerRef3 = useRef<HTMLDivElement>(null);

  const [phase, setPhase] = useState<ViewerPhase>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (ui.error instanceof Error) {
    throw ui.error;
  }

  useEffect(() => {
    const viewer1 = viewerRef1.current;
    if (!viewer1) return;
    viewer1.addEventListener(
      Enums.Events.ANNOTATION_RENDERED,
      crosshairRenderingListener,
    );
    viewer1.addEventListener(EVENTS.VOI_MODIFIED, voiModifiedListener);
    return () => {
      viewer1.removeEventListener(
        Enums.Events.ANNOTATION_RENDERED,
        crosshairRenderingListener,
      );
      viewer1.removeEventListener(EVENTS.VOI_MODIFIED, voiModifiedListener);
    };
  });

  useEffect(() => {
    if (!patient_id || !acquisition) return;

    let cancelled = false;

    try {
      cstSegmentation.removeAllSegmentationRepresentations();
      annotation.state.removeAllAnnotations();

      const { renderingEngineId, viewportId1, viewportId2, viewportId3 } =
        getIds(id);
      const re = getRenderingEngine(renderingEngineId);
      if (re) {
        [viewportId1, viewportId2, viewportId3].forEach((vpId) => {
          const vp = re.getViewport(vpId) as any;
          vp?.removeAllActors?.();
        });
      }
      cache.purgeCache();
    } catch (e) {}

    viewerState.instanceMetadata = null;
    viewerState.segmentsPerSeries = [];
    viewerState.niftiDatasets = [];
    viewerState.niftiBases = {};
    viewerState.niftiActiveModality = null;
    viewerState.niftiPetImages = [];
    viewerState.niftiPetMasks = [];
    viewerState.pendingSeriesUID = null;
    viewerState.focusedMarkerCode = null;

    async function autoDiscoverAndLoad() {
      setPhase("discovering");
      setErrorMsg(null);

      try {
        const result = await discoverNiftiFiles(
          ENTRYPOINT,
          patient_id,
          acquisition,
        );
        if (cancelled) return;

        if (result.bases.length === 0) {
          throw new Error(
            `No NIfTI base images found (${result.allFiles.length} files matched total)`,
          );
        }

        const basesMap: Record<string, string> = {};
        for (const b of result.bases) {
          if (b.entry.modality && !basesMap[b.entry.modality])
            basesMap[b.entry.modality] = b.url;
        }
        viewerState.niftiBases = basesMap;
        const pet = buildPetFiles([...result.bases, ...result.segmentations]);
        viewerState.niftiPetImages = pet.images;
        viewerState.niftiPetMasks = pet.masks;

        const mod = result.activeModality;
        viewerState.niftiActiveModality = mod;

        const base =
          result.bases.find((b) => b.entry.modality === mod) ?? result.bases[0];

        const segUrls: Record<string, string> = {};
        for (const seg of result.segmentations) {
          // PET masks are loaded on demand by the PET controls.
          if (isPetModality(seg.entry.modality)) continue;
          segUrls[seg.entry.setType] = seg.url;
        }

        setPhase("loading");
        viewerState.instanceMetadata = buildNiftiMetadata(base.url, {
          studyId: `${result.infos.subject}_${acquisition}`,
          segUrls: Object.keys(segUrls).length > 0 ? segUrls : undefined,
          modality: mod,
        }) as any;
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setErrorMsg(msg);
        setPhase("error");
        console.error("[Viewer] Auto-discovery failed:", e);
      }
    }

    autoDiscoverAndLoad();
    return () => {
      cancelled = true;
    };
  }, [patient_id, acquisition, id]);

  useEffect(() => {
    const rawMeta = viewerState.instanceMetadata;
    if (!rawMeta || phase !== "loading") return;

    uiState.loading = true;

    initViewer(id, [viewerRef1, viewerRef2, viewerRef3], rawMeta, notify)
      .then(() => setPhase("ready"))
      .catch((e) => {
        uiState.error = e;
        setPhase("error");
        setErrorMsg(e instanceof Error ? e.message : String(e));
      })
      .finally(() => (uiState.loading = false));

    uiState.error = undefined;
  }, [snap.instanceMetadata, id, notify, phase]);

  return (
    <>
      <ViewerPane
        id={`viewer_${id}_1`}
        orientationLabels={{ top: "S", left: "R" }}
        ref={viewerRef1}
        expandedPane={ui.expandedPane}
        status={
          phase === "discovering"
            ? "Finding images…"
            : phase === "loading"
            ? "Loading image…"
            : phase === "error"
            ? errorMsg ?? "Image loading failed"
            : undefined
        }
      />
      <ViewerPane
        id={`viewer_${id}_2`}
        orientationLabels={{ top: "S", left: "A" }}
        ref={viewerRef2}
        expandedPane={ui.expandedPane}
      />
      <ViewerPane
        id={`viewer_${id}_3`}
        orientationLabels={{ top: "A", left: "R" }}
        ref={viewerRef3}
        expandedPane={ui.expandedPane}
      />
    </>
  );
}

interface ViewerPaneProps {
  id: string;
  orientationLabels: { top: string; left: string };
  expandedPane: string | null;
  status?: string;
}

export const ViewerPane = forwardRef(function ViewerPane(
  { id, orientationLabels, expandedPane, status }: ViewerPaneProps,
  ref: Ref<HTMLDivElement>,
) {
  let className = "h-[45vh] rounded-lg overflow-hidden bg-black relative";
  if (expandedPane === id) {
    className =
      "h-[90vh] w-[85vw] rounded-lg overflow-hidden bg-black relative";
  } else if (expandedPane !== null) {
    className = "hidden";
  }

  return (
    <div
      id={id}
      data-testid={id}
      onContextMenu={(e) => e.preventDefault()}
      onDoubleClick={() => toggleExpandedPane(id)}
      className={className}
      ref={ref}
    >
      {status && (
        <div
          role="status"
          className="absolute top-8 left-3 right-3 z-10 rounded bg-black/80 p-3 text-white"
        >
          {status}
        </div>
      )}
      <OrientationLabel
        top={orientationLabels.top}
        left={orientationLabels.left}
      />
    </div>
  );
});

function OrientationLabel({ top, left }: { top: string; left: string }) {
  const snap = useSnapshot(settingsState);
  if (!snap.showOrientationLabel) return null;

  return (
    <>
      <span className="absolute top-1 left-[49.3%] z-10">{top}</span>
      <span className="absolute top-[47.5%] left-1 z-10">{left}</span>
    </>
  );
}

export default Viewer;
